import type { NextPage } from 'next';
import Head from 'next/head';
import React from 'react';

import { Currency } from '@src/@types/currency';
import { PageContext } from '@src/config';
import type { IPageContextConfig } from '@src/config/PageContext';
import { ListKeys } from '@src/constants/microcredits';
import Disclaimer from '@src/containers/landing/Disclaimer';
import ProductList from '@src/containers/list/ProductList';
import { useListPage } from '@src/hooks/useListPage';
import { kzContextPageRoute } from '@src/shared/constants/pageRoutes';
import { Layout } from 'app/Layout';
import { PhoneNumberChecker } from 'features/PhoneNumberChecker';
import { linkForConsentToProcessingPersonalDataForKazakhstan } from 'shared/config/linkForConsentToProcessingPersonalDataForKazakhstan';
import { validateKazakhstanPhoneNumber } from 'shared/ui';
import { BrandingHeaders } from 'widgets/BrandingHeaders';
import { LightweightCard } from 'widgets/LightweightCard';
import { MobileSubHeader } from 'widgets/MobileSubHeader';
import { SeoComponents } from 'widgets/SeoComponents';

import { getKazakhstanPageData } from '../lib';

import { CARD_CONTENT } from './KazakhstanPage.config';
import styles from './KazakhstanPage.module.scss';
import { KazakhstanPageFooter } from './KazakhstanPageFooter';
import { KazakhstanPageHeader } from './KazakhstanPageHeader';

const pageConfig: IPageContextConfig = {
  listKey: ListKeys.KAZAKHSTAN,
  source: 'search',
  withMonetizationCardLogoClick: true,
};

export const KazakhstanPage: NextPage = () => {
  const { filter, seo, pathname, isMobile, hasUtmLabel, asPath } = useListPage(ListKeys.KAZAKHSTAN);

  const [pathWithoutQuery] = asPath.split('?');
  const isContextPage = pathWithoutQuery === kzContextPageRoute;

  return (
    <Layout footer={<KazakhstanPageFooter />} header={<KazakhstanPageHeader />} showMobileAppBanner={false}>
      <PageContext.Provider value={pageConfig}>
        <Head>
          <meta name="robots" content="index, follow" />
          <link rel="alternate" href={`https://www.sravni.ru${pathname}`} hrefLang="ru-kz" />
        </Head>

        <BrandingHeaders
          containerClassName={styles.page_header}
          afterHeaders={
            <PhoneNumberChecker
              phoneNumberValidate={validateKazakhstanPhoneNumber}
              termsLink={linkForConsentToProcessingPersonalDataForKazakhstan}
            />
          }
        />

        {isMobile && !isContextPage && <MobileSubHeader withAnchorList={false} />}

        <ProductList
          filter={filter}
          gridLayout
          renderCard={(props) => (
            <LightweightCard
              {...props}
              hasUtmLabel={hasUtmLabel}
              contentSettings={CARD_CONTENT}
              currency={Currency.KZT}
            />
          )}
        />

        {seo && (
          <SeoComponents
            seo={seo}
            breadcrumbs={seo.breadcrumbs}
            breadcrumbsClassName={styles.breadcrumbs}
            prefooter={seo.prefooter}
            showSubheader={false}
            showAnchorLinks={false}
          />
        )}
        <Disclaimer />
      </PageContext.Provider>
    </Layout>
  );
};

KazakhstanPage.getInitialProps = getKazakhstanPageData;
